import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import XeroService from "services/xero.service";
import HandleResponse from "../HandleResponse";
import HandleError from "../HandleError";
import { computeAnalyticsFromLogs } from "../../pages/xero/utils/computeAnalyticsFromLogs";

const parseAnalyticsPayload = (data) => (data?.summary ? data : data?.data ?? data);

const buildFilterArgs = (formValues = {}) => ({
  status: formValues.status ?? "",
  invoice_numbers: formValues.invoice_numbers ?? [],
  paidby: formValues.paidby ?? [],
});

export const XeroUpdateKeyApi = createAsyncThunk("xero/updatekey", async (formValues, thunkAPI) => {
  try {
    const response = await XeroService.updateXeroKey(formValues);
    return response.data;
  } catch (error) {
    if (error.response) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
    return thunkAPI.rejectWithValue(error.message);
  }
});

export const XeroConnectionApi = createAsyncThunk("xero/connection", async (_, thunkAPI) => {
  try {
    const response = await XeroService.fetchConnection();
    return HandleResponse(thunkAPI, response, "connection");
  } catch (error) {
    return HandleError(thunkAPI, error, "connection");
  }
});

export const XeroDisconnectApi = createAsyncThunk("xero/disconnect", async (_, thunkAPI) => {
  try {
    const response = await XeroService.disconnect();
    return HandleResponse(thunkAPI, response);
  } catch (error) {
    return HandleError(thunkAPI, error, "disconnect");
  }
});

export const XeroFilterOptionsApi = createAsyncThunk("xero/filteroptions", async (_, thunkAPI) => {
  try {
    const response = await XeroService.fetchFilterOptions();
    return HandleResponse(thunkAPI, response, "filteroptions");
  } catch (error) {
    return HandleError(thunkAPI, error, "filteroptions");
  }
});

export const XeroAnalyticsApi = createAsyncThunk("xero/analytics", async (formValues, thunkAPI) => {
  const filters = buildFilterArgs(formValues);
  const period = formValues.period ?? "all";

  const loadFromLogs = async () => {
    const logsRes = await XeroService.fetchLogs({
      ...filters,
      period,
      page: 1,
      limit: 500,
    });
    if (logsRes.status === 200 && logsRes.data?.data?.length) {
      return computeAnalyticsFromLogs(logsRes.data.data, period);
    }
    return null;
  };

  try {
    const response = await XeroService.fetchAnalytics({ ...filters, period });
    if (response.status === 200) {
      const payload = parseAnalyticsPayload(response.data);
      if (payload?.summary) {
        return payload;
      }
      const fallback = await loadFromLogs();
      if (fallback) {
        return fallback;
      }
      return payload;
    }
  } catch (error) {
    const fallback = await loadFromLogs();
    if (fallback) {
      return fallback;
    }
    return HandleError(thunkAPI, error, "analytics");
  }

  return thunkAPI.rejectWithValue({ message: "Unable to load analytics" });
});

export const XeroLogsListApi = createAsyncThunk("xero/logslist", async (formValues, thunkAPI) => {
  try {
    const response = await XeroService.fetchLogs(formValues);
    return HandleResponse(thunkAPI, response, "logslist");
  } catch (error) {
    return HandleError(thunkAPI, error, "logslist");
  }
});

export const XeroResyncApi = createAsyncThunk("xero/resync", async (logId, thunkAPI) => {
  try {
    const response = await XeroService.resyncLog(logId);
    return HandleResponse(thunkAPI, response, "resync");
  } catch (error) {
    return HandleError(thunkAPI, error, "resync");
  }
});

const initialState = {
  isOpenedXeroForm: "",
  isLoading: false,
  error: null,
  connection: null,
  connectionRequestId: null,
  analytics: null,
  analyticsPeriod: "all",
  filterOptions: {
    invoice_numbers: [],
    paidby_options: [],
  },
  invoiceFilter: [],
  paidbyFilter: [],
  logsList: {
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 20,
    from: 0,
    to: 0,
    next_page_url: null,
    prev_page_url: null,
    total: 0,
  },
  logsPage: 1,
  logsPerPage: 20,
  statusFilter: "",
  resyncingLogId: null,
  isApiStatus: {
    XeroConnectionApi: "",
    XeroDisconnectApi: "",
    XeroFilterOptionsApi: "",
    XeroAnalyticsApi: "",
    XeroLogsListApi: "",
    XeroResyncApi: "",
  },
};

const xeroSlice = createSlice({
  name: "xero",
  initialState,
  reducers: {
    OpenXeroForm: (state, action) => {
      state.isOpenedXeroForm = action.payload;
    },
    SetXeroStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    SetXeroInvoiceFilter: (state, action) => {
      state.invoiceFilter = action.payload;
    },
    SetXeroPaidbyFilter: (state, action) => {
      state.paidbyFilter = action.payload;
    },
    SetXeroLogsPage: (state, action) => {
      state.logsPage = action.payload;
    },
    SetXeroLogsPerPage: (state, action) => {
      state.logsPerPage = action.payload;
    },
    SetXeroAnalyticsPeriod: (state, action) => {
      state.analyticsPeriod = action.payload;
    },
    SetXeroAnalytics: (state, action) => {
      state.analytics = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(XeroUpdateKeyApi.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(XeroUpdateKeyApi.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(XeroUpdateKeyApi.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(XeroConnectionApi.pending, (state, action) => {
        state.isApiStatus.XeroConnectionApi = "loading";
        state.connectionRequestId = action.meta.requestId;
      })
      .addCase(XeroConnectionApi.fulfilled, (state, action) => {
        if (state.connectionRequestId !== action.meta.requestId) return;
        state.connectionRequestId = null;
        state.isApiStatus.XeroConnectionApi = "succeeded";
        state.connection = action.payload;
      })
      .addCase(XeroConnectionApi.rejected, (state, action) => {
        if (state.connectionRequestId !== action.meta.requestId) return;
        state.connectionRequestId = null;
        state.isApiStatus.XeroConnectionApi = "failed";
      })
      .addCase(XeroDisconnectApi.pending, (state) => {
        state.isApiStatus.XeroDisconnectApi = "loading";
      })
      .addCase(XeroDisconnectApi.fulfilled, (state) => {
        state.isApiStatus.XeroDisconnectApi = "succeeded";
        // The confirmed write takes precedence over stale profile data and
        // connection reads that started before it completed.
        state.connection = { is_connected: false };
        state.connectionRequestId = null;
        state.isApiStatus.XeroConnectionApi = "";
      })
      .addCase(XeroDisconnectApi.rejected, (state) => {
        state.isApiStatus.XeroDisconnectApi = "failed";
      })
      .addCase(XeroFilterOptionsApi.fulfilled, (state, action) => {
        state.isApiStatus.XeroFilterOptionsApi = "succeeded";
        state.filterOptions = action.payload || state.filterOptions;
      })
      .addCase(XeroAnalyticsApi.pending, (state) => {
        state.isApiStatus.XeroAnalyticsApi = "loading";
      })
      .addCase(XeroAnalyticsApi.fulfilled, (state, action) => {
        state.isApiStatus.XeroAnalyticsApi = "succeeded";
        state.analytics = action.payload;
        const period = action.meta.arg?.period;
        if (period !== undefined && period !== null) {
          state.analyticsPeriod =
            period === 0 || period === "0" ? "all" : String(period);
        }
      })
      .addCase(XeroAnalyticsApi.rejected, (state) => {
        state.isApiStatus.XeroAnalyticsApi = "failed";
      })
      .addCase(XeroLogsListApi.pending, (state) => {
        state.isApiStatus.XeroLogsListApi = "loading";
      })
      .addCase(XeroLogsListApi.fulfilled, (state, action) => {
        state.isApiStatus.XeroLogsListApi = "succeeded";
        state.logsList = action.payload;
        if (action.meta.arg?.page) {
          state.logsPage = action.meta.arg.page;
        }
        if (action.meta.arg?.limit) {
          state.logsPerPage = action.meta.arg.limit;
        }
      })
      .addCase(XeroLogsListApi.rejected, (state) => {
        state.isApiStatus.XeroLogsListApi = "failed";
      })
      .addCase(XeroResyncApi.pending, (state, action) => {
        state.isApiStatus.XeroResyncApi = "loading";
        state.resyncingLogId = action.meta.arg;
      })
      .addCase(XeroResyncApi.fulfilled, (state) => {
        state.isApiStatus.XeroResyncApi = "succeeded";
        state.resyncingLogId = null;
      })
      .addCase(XeroResyncApi.rejected, (state) => {
        state.isApiStatus.XeroResyncApi = "failed";
        state.resyncingLogId = null;
      });
  },
});

export const {
  OpenXeroForm,
  SetXeroStatusFilter,
  SetXeroInvoiceFilter,
  SetXeroPaidbyFilter,
  SetXeroLogsPage,
  SetXeroLogsPerPage,
  SetXeroAnalyticsPeriod,
  SetXeroAnalytics,
} = xeroSlice.actions;
export default xeroSlice.reducer;
